// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Menu  (route /menu)
// The only promotion shown on the menu is a REAL one: the signed-in customer's own unused welcome
// coupon (GET /api/me/welcome-coupon, redeemable at checkout). Guests, customers whose coupon is used,
// and any load failure see nothing — the menu never advertises an offer it can't honour.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Gift } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { fetchWelcomeCoupon, type WelcomeCoupon } from "../../utils/coupon";
import { formatInr } from "../../utils/priceFormat";

export default function CouponBanner() {
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const [coupon, setCoupon] = useState<WelcomeCoupon | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    fetchWelcomeCoupon().then(
      (fetched) => !cancelled && setCoupon(fetched.redeemed ? null : fetched),
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  if (!coupon) return null;
  return (
    <aside aria-label="Your welcome coupon" className="flex items-center gap-4 rounded-2xl bg-ocean-800 p-4 text-white shadow-sm">
      <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15">
        <Gift className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <p className="font-display text-lg font-bold leading-tight">
          {formatInr(coupon.amountOffInr)} off + free delivery
        </p>
        <p className="text-sm text-foam-100/90">
          On your first order of {formatInr(coupon.minOrderInr)}+. Code <span className="font-mono font-bold tracking-wider">{coupon.code}</span>, applied at checkout.
        </p>
      </div>
    </aside>
  );
}
